"use strict";
/* globals module */
/**
 * @module articleInstanceCommentRepliesRoute
 * @description Baasic Article Instance Comment Replies Route Definition provides Baasic route templates which can be expanded to Baasic REST URIs. Various services can use Baasic Article Instance Comment Replies Route Service to obtain needed routes while other routes will be obtained through HAL. By convention, all route services use the same function names as their corresponding services.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
var common_1 = require("../../../common");
;
var contracts_1 = require("../../../core/contracts");
var ArticleInstanceCommentRepliesRoute = /** @class */ (function (_super) {
    tslib_1.__extends(ArticleInstanceCommentRepliesRoute, _super);
    function ArticleInstanceCommentRepliesRoute(appOptions) {
        var _this = _super.call(this, appOptions) || this;
        _this.appOptions = appOptions;
        _this.findRoute = 'articles/{articleId}/comments/{commentId}/replies/{?searchQuery,statuses,page,rpp,sort,embed,fields}';
        _this.getRoute = 'articles/{articleId}/comments/{commentId}/replies/{id}/{?embed,fields}';
        _this.createRoute = 'articles/{articleId}/comments/{commentId}/replies';
        _this.updateRoute = 'articles/{articleId}/comments/{id}';
        _this.approveRoute = 'articles/{articleId}/comments/{commentId}/replies/{id}/approve';
        _this.unapproveRoute = '/articles/{articleId}/comments/{commentId}/replies/{id}/unapprove';
        _this.flagRoute = 'articles/{articleId}/comments/{commentId}/replies/{id}/flag';
        _this.unflagRoute = 'articles/{articleId}/comments/{commentId}/replies/{id}/unflag';
        _this.reportRoute = 'articles/{articleId}/comments/{commentId}/replies/{id}/report';
        _this.deleteRoute = 'articles/{articleId}/comments/{id}';
        _this.deleteAllRoute = 'articles/{articleId}/comments';
        _this.unreportRoute = 'articles/{articleId}/comments/{commentId}/replies/{id}/unreport';
        _this.spamRoute = 'articles/{articleId}/comments/{commentId}/replies/{id}/spam';
        _this.unspamRoute = '/articles/{articleId}/comments/{commentId}/replies/{id}/unspam';
        return _this;
    }
    /**
     * Parses find route which can be expanded with additional options. Supported items are:
     * - `searchQuery` - A string value used to identify article comment reply resources using the phrase search.
     * - `page` - A value used to set the page number, i.e. to retrieve certain article comment reply subset from the storage.
     * - `rpp` - A value used to limit the size of result set per page.
     * - `sort` - A string used to set the article comment reply property to sort the result collection by.
     * - `embed` - Comma separated list of resources to be contained within the current representation.
     * - `statuses` - Comma separated list of article comment reply states that specify where search should be done (Allowed states: Approved, Spam, Reported, Flagged and UnApproved).
     * @method
     * @param articleId Article id which uniquely identifies article whose comment reply resources need to be retrieved.
     * @param commentId Comment id which uniquely identifies comment whose reply resources need to be retrieved.
     * @param options Query resource options.
     * @example articleCommentRepliesRoute.find({ searchQuery: '<search-phrase>' });
     **/
    ArticleInstanceCommentRepliesRoute.prototype.find = function (articleId, commentId, options) {
        var params = this.modelMapper.findParams(options);
        params.articleId = articleId;
        params.commentId = commentId;
        return _super.prototype.baseFind.call(this, this.findRoute, options);
    };
    /**
     * Parses get route which can be expanded with additional options. Supported items are:
     * - `id` - Id which uniquely identifies article comment reply resource that needs to be retrieved.
     * - `embed` - Comma separated list of resources to be contained within the current representation.
     * @method
     * @example articleCommentRepliesRoute.get({ id: '<comment-reply-id>' });
     **/
    ArticleInstanceCommentRepliesRoute.prototype.get = function (articleId, commentId, replyId, options) {
        var params = this.utility.extend({}, options);
        params.articleId = articleId;
        params.commentId = commentId;
        return _super.prototype.baseGet.call(this, this.getRoute, replyId, params);
    };
    /**
     * Parses create article comment reply route; this URI template does not support any additional items.
     * @method
     * @example articleCommentRepliesRoute.create(data);
     **/
    ArticleInstanceCommentRepliesRoute.prototype.create = function (articleId, data) {
        var params = this.utility.extend({}, data);
        params.articleId = articleId;
        return _super.prototype.baseCreate.call(this, this.createRoute, params);
    };
    ArticleInstanceCommentRepliesRoute.prototype.update = function (data) {
        return _super.prototype.baseUpdate.call(this, this.updateRoute, data);
    };
    ArticleInstanceCommentRepliesRoute.prototype.approve = function (data) {
        return _super.prototype.baseUpdate.call(this, this.approveRoute, data, undefined, 'comment-approve');
    };
    ArticleInstanceCommentRepliesRoute.prototype.unapprove = function (data) {
        return _super.prototype.baseUpdate.call(this, this.unapproveRoute, data, undefined, 'comment-unapprove');
    };
    ArticleInstanceCommentRepliesRoute.prototype.flag = function (data) {
        return _super.prototype.baseUpdate.call(this, this.flagRoute, data, undefined, 'comment-flag');
    };
    ArticleInstanceCommentRepliesRoute.prototype.unflag = function (data) {
        return _super.prototype.baseUpdate.call(this, this.unflagRoute, data, undefined, 'comment-unflag');
    };
    ArticleInstanceCommentRepliesRoute.prototype.report = function (data) {
        return _super.prototype.baseUpdate.call(this, this.reportRoute, data, undefined, 'comment-report');
    };
    ArticleInstanceCommentRepliesRoute.prototype.delete = function (data) {
        return _super.prototype.baseDelete.call(this, this.deleteRoute, data);
    };
    ArticleInstanceCommentRepliesRoute.prototype.deleteAll = function (data) {
        return _super.prototype.baseUpdate.call(this, this.deleteAllRoute, data, undefined, 'delete-comments-by-article');
    };
    ArticleInstanceCommentRepliesRoute.prototype.unreport = function (data) {
        return _super.prototype.baseUpdate.call(this, this.unreportRoute, data, undefined, 'comment-unreport');
    };
    ArticleInstanceCommentRepliesRoute.prototype.spam = function (data) {
        return _super.prototype.baseUpdate.call(this, this.spamRoute, data, undefined, 'comment-spam');
    };
    ArticleInstanceCommentRepliesRoute.prototype.unspam = function (data) {
        return _super.prototype.baseUpdate.call(this, this.unspamRoute, data, undefined, 'comment-unspam');
    };
    var _a;
    ArticleInstanceCommentRepliesRoute = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(contracts_1.TYPES.IAppOptions)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof contracts_1.IAppOptions !== "undefined" && contracts_1.IAppOptions) === "function" ? _a : Object])
    ], ArticleInstanceCommentRepliesRoute);
    return ArticleInstanceCommentRepliesRoute;
}(common_1.BaseRoute));
exports.ArticleInstanceCommentRepliesRoute = ArticleInstanceCommentRepliesRoute;
/**
 * @overview
 ***Notes:**
 - Refer to the [REST API documentation](https://github.com/Baasic/baasic-rest-api/wiki) for detailed information about available Baasic REST API end-points.
 - [URI Template](https://github.com/Baasic/uritemplate-js) syntax enables expanding the Baasic route templates to Baasic REST URIs providing it with an object that contains URI parameters.
 - All end-point objects are transformed by the associated route service.
*/ 
